/**
 * Control characters and special whitespace that can bypass protocol checks.
 * From DOMPurify: https://github.com/cure53/DOMPurify/blob/main/src/regexp.js
 */
const DANGEROUS_WHITESPACE =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;

const EXPLICIT_HTTP_PROTOCOL = /^https?:\/\//i;

export interface SanitizeHrefOptions {
  fallback?: string | undefined | null;
  allowMailto?: boolean;
  allowRelative?: boolean;
  allowedDomains?: string[];
}

/**
 * Sanitizes a URL to prevent XSS via dangerous protocols (javascript:, data:, vbscript:, etc.).
 *
 * Only allows http: and https: protocols. Relative URLs, hash fragments, and
 * query strings are considered safe and passed through unchanged.
 *
 * Returns the original URL if safe. If dangerous or invalid, attempts the fallback.
 * Returns empty string when both values are dangerous or invalid.
 *
 * Use this for any URL from an untrusted source (API responses, user input, etc.)
 * before assigning to href attributes or window.location.
 *
 * @example
 * sanitizeUrl('https://www.deviantart.com/user')  // 'https://www.deviantart.com/user'
 * sanitizeUrl('javascript:alert(1)')              // ''
 * sanitizeUrl('/gallery/123')                     // '/gallery/123'
 * sanitizeUrl('javascript:alert(1)', '/gallery')  // '/gallery'
 * sanitizeUrl(undefined)                          // ''
 */
function sanitizeUrlInternal(url: string | undefined | null): string {
  if (!url) {
    return '';
  }

  // Strip control characters that can bypass protocol detection
  // e.g., "java\tscript:" or "java\x00script:"
  const normalized = url.replace(DANGEROUS_WHITESPACE, '');

  try {
    // Use a dummy base so relative URLs (inherently safe) resolve to https:
    const parsed = new URL(normalized, 'https://x.invalid');
    // Block userinfo-style URLs to prevent hidden destination spam patterns.
    if (parsed.username || parsed.password) {
      return '';
    }

    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return url;
    }
  } catch {
    // URL parsing failed - not a valid URL
  }

  return '';
}

function isSafeRelativeHref(url: string): boolean {
  return url.startsWith('/') || url.startsWith('#') || url.startsWith('?');
}

function sanitizeHrefWithOptions(
  url: string | undefined | null,
  { allowMailto = true, allowRelative = true, allowedDomains }: SanitizeHrefOptions
): string {
  if (!url) {
    return '';
  }

  if (isSafeRelativeHref(url)) {
    return allowRelative ? url : '';
  }

  const normalized = url.replace(DANGEROUS_WHITESPACE, '');

  try {
    const parsed = new URL(normalized, 'https://x.invalid');
    if (parsed.username || parsed.password) {
      return '';
    }

    const isExplicitHttpUrl = EXPLICIT_HTTP_PROTOCOL.test(normalized);
    if (isExplicitHttpUrl && (parsed.protocol === 'http:' || parsed.protocol === 'https:')) {
      if (
        allowedDomains &&
        allowedDomains.length > 0 &&
        !allowedDomains.some(
          domain => parsed.hostname === domain || parsed.hostname.endsWith('.' + domain)
        )
      ) {
        return '';
      }
      return url;
    }

    if (allowMailto && parsed.protocol === 'mailto:') {
      return url;
    }
  } catch {
    // URL parsing failed - not a valid URL
  }

  return '';
}

export function sanitizeUrl(url: string | undefined | null): string;
export function sanitizeUrl(
  url: string | undefined | null,
  fallback: string | undefined | null
): string;
export function sanitizeUrl(
  url: string | undefined | null,
  fallback: string | undefined | null = ''
): string {
  return sanitizeUrlInternal(url) || sanitizeUrlInternal(fallback);
}

export function sanitizeHref(url: string | undefined | null): string | undefined;
export function sanitizeHref(
  url: string | undefined | null,
  options: SanitizeHrefOptions
): string | undefined;
export function sanitizeHref(
  url: string | undefined | null,
  options: SanitizeHrefOptions = {}
): string | undefined {
  const { fallback = '' } = options;
  return (
    sanitizeHrefWithOptions(url, options) || sanitizeHrefWithOptions(fallback, options) || undefined
  );
}

/**
 * Sanitizes a URL and navigates to it via window.location.href.
 * If the URL is dangerous or empty, navigation is skipped.
 *
 * Replaces the common pattern:
 *   const safe = sanitizeUrl(url);
 *   if (safe) { window.location.href = safe; }
 *
 * @example
 * navigateToSanitizedUrl(apiResponse.redirectUrl);
 * navigateToSanitizedUrl('javascript:alert(1)'); // no-op
 */
export function navigateToSanitizedUrl(url: string | undefined | null): void;
export function navigateToSanitizedUrl(
  url: string | undefined | null,
  fallback: string | undefined | null
): void;
export function navigateToSanitizedUrl(
  url: string | undefined | null,
  fallback: string | undefined | null = ''
): void {
  const safe = sanitizeUrl(url, fallback);
  if (safe) {
    window.location.href = safe;
  }
}

/**
 * Sanitizes a URL and navigates to it via window.location.replace.
 * If the URL is dangerous or empty, navigation is skipped.
 *
 * @example
 * replaceWithSanitizedUrl(apiResponse.redirectUrl);
 * replaceWithSanitizedUrl('javascript:alert(1)', '/gallery'); // navigates to /gallery
 */
export function replaceWithSanitizedUrl(url: string | undefined | null): void;
export function replaceWithSanitizedUrl(
  url: string | undefined | null,
  fallback: string | undefined | null
): void;
export function replaceWithSanitizedUrl(
  url: string | undefined | null,
  fallback: string | undefined | null = ''
): void {
  const safe = sanitizeUrl(url, fallback);
  if (safe) {
    window.location.replace(safe);
  }
}
